import React, { useState, useRef } from 'react'
import { Modal, ModalBody, ModalHeader } from 'reactstrap'
import {
  IconX,
  IconUpload,
  IconFileText,
  IconTable,
  IconFile,
  IconPhoto
} from '@tabler/icons-react'
//@ts-ignore
import { Button } from 'dsm-react-lib'
import { useSelector } from 'react-redux'

interface FileUploadModalProps {
  isOpen: boolean
  toggle: () => void
  preselectedFiles?: File[]
}

interface UploadFile {
  id: string
  name: string
  size: number
  type: string
  file: File
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  isOpen,
  toggle,
  preselectedFiles = []
}) => {
  const [selectedFiles, setSelectedFiles] = useState<UploadFile[]>([])
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Allowed file types
  const allowedTypes = [
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel.sheet.macroEnabled.12'
  ]

  const allowedExtensions = ['.pdf', '.txt', '.doc', '.docx', '.xls', '.xlsx', '.xlsm']

  // Validate file type
  const isFileTypeAllowed = (file: File): boolean => {
    const extension = `.${file.name.split('.').pop()?.toLowerCase()}`
    return allowedTypes.includes(file.type) || allowedExtensions.includes(extension)
  }

  // Initialize with preselected files when modal opens
  React.useEffect(() => {
    if (isOpen && preselectedFiles.length > 0) {
      const validFiles: UploadFile[] = []
      const rejected: string[] = []

      preselectedFiles.forEach((file, index) => {
        if (isFileTypeAllowed(file)) {
          validFiles.push({
            id: `${Date.now()}_${index}`,
            name: file.name,
            size: file.size,
            type: file.type,
            file
          })
        } else {
          rejected.push(file.name)
        }
      })

      setSelectedFiles(validFiles)
      setRejectedFiles(rejected)
    }
  }, [isOpen, preselectedFiles])

  // Get file icon based on file type
  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase()

    switch (extension) {
      case 'pdf':
        return <IconFileText size={20} className="text-red-500" />
      case 'csv':
        return <IconTable size={20} className="text-green-500" />
      case 'docx':
      case 'doc':
        return <IconFile size={20} className="text-blue-500" />
      case 'xlsx':
      case 'xls':
        return <IconTable size={20} className="text-green-600" />
      case 'txt':
        return <IconFileText size={20} className="theme-text-subheading" />
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <IconPhoto size={20} className="text-purple-500" />
      default:
        return <IconFile size={20} className="theme-icon-muted" />
    }
  }

  // Handle file selection from input
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    const validFiles: UploadFile[] = []
    const rejected: string[] = []

    files.forEach((file, index) => {
      if (isFileTypeAllowed(file)) {
        validFiles.push({
          id: `${Date.now()}_${index}`,
          name: file.name,
          size: file.size,
          type: file.type,
          file
        })
      } else {
        rejected.push(file.name)
      }
    })

    setSelectedFiles(validFiles)
    setRejectedFiles(rejected)
    // Reset the input value to allow selecting the same files again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Remove a file from the list
  const removeFile = (fileId: string) => {
    setSelectedFiles((prev) => prev.filter((file) => file.id !== fileId))
  }

  // Handle modal close
  const handleClose = () => {
    setSelectedFiles([])
    setRejectedFiles([])
    toggle()
  }

  // Handle confirm upload
  const handleConfirmUpload = () => {
    // Here you would implement the actual upload logic
    handleClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      size="lg"
      contentClassName="h-[80vh] max-w-[60vw] flex flex-col">
      <ModalHeader
        className={`!justify-between !border-0 !flex !items-center chat-primary theme-bg flex-shrink-0`}
        toggle={handleClose}
        close={
          <IconX
            size={24}
            onClick={handleClose}
            className="text-white transition-colors duration-300 ease-in-out hover:text-[var(--text-disabled)] cursor-pointer"
            data-testid="close-button"
          />
        }>
        <div className="flex items-center gap-1">
          <IconFile size={20} className="text-white" />
          <span className="text-white">Upload files</span>
        </div>
      </ModalHeader>

      <ModalBody className="theme-surface p-2 flex-1 overflow-hidden flex flex-col">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          accept="*/*"
        />

        {/* Description */}
        <p className="theme-text-subheading text-lg mb-2">
          Review the files you&apos;ve selected before confirming the upload.
        </p>

        {/* File count */}
        <div className="flex theme-surface-muted items-center gap-1 mb-1">
          <IconFile size={22} className="theme-text-subheading" />
          <span className="font-medium text-2xl theme-text-body">
            <b>{selectedFiles.length}</b> files selected
          </span>
        </div>

        {/* Conditional rendering: Browse button or File list */}
        {selectedFiles.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <Button
              label="Browse files"
              onClick={() => fileInputRef.current?.click()}
              color="primary"
              icon={<IconUpload size={16} />}
            />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto mb-1">
            <div className="divide-y divide-[var(--divider-color)]">
              {selectedFiles.map((file, index) => (
                <div
                  key={file.id}
                  className="flex items-center gap-1 py-50 px-1 theme-hover-surface">
                  {getFileIcon(file.name)}
                  <span className="flex-1 text-lg theme-text-body truncate">{file.name}</span>
                  <div
                    onClick={() => removeFile(file.id)}
                    className="cursor-pointer theme-icon-muted theme-hover-surface p-1.5 transition-all duration-200"
                    title="Remove file">
                    <IconX size={18} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer buttons */}
        <div className="flex justify-end gap-1">
          <Button label="Cancel" variant="outline" color="gray" onClick={handleClose} />
          <Button
            label="Confirm upload"
            color="primary"
            disabled={selectedFiles.length === 0}
            onClick={handleConfirmUpload}
          />
        </div>
      </ModalBody>
    </Modal>
  )
}

export default FileUploadModal
